import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useAdminAuth } from './hooks/useAdminAuth';
import PublicLayout from './pages/public/components/Layout';
import MyAppLayout from './pages/MyApp/pages/Layout';
import { useLanguage } from './hooks/useLanguage';
import { myAppRoutes } from './pages/MyApp/routes';
import { adminRoutes } from './admin/routes';
import LoginPage from './pages/public/LoginPage';
import { db } from './services/AppDataServices/data_oscbase';

// Pages publiques
import Home from './pages/public/Home';
import Contact from './pages/public/Contact';

/**
 * Composant pour les routes protégées de l'application principale (MyApp)
 * Vérifie si l'utilisateur est connecté
 */
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { lang } = useLanguage();

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (!user) {
    return <Navigate to={`/${lang}/login`} replace />;
  }

  return <>{children}</>;
};

/**
 * Composant pour les routes protégées de l'administration
 * Vérifie si l'administrateur est connecté
 */
const AdminRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();

  if (loading) {
    return <div>Chargement...</div>;
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Composant pour les routes publiques uniquement
 * Permet l'accès à tous les utilisateurs
 */
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return <div>Chargement...</div>;
  }

  return <>{children}</>;
};

// Composant pour gérer la direction et les polices selon la langue
const LanguageLayout = ({ children }) => {
  const { lang } = useLanguage();
  
  React.useEffect(() => {
    const currentLang = lang || 'fr';
    document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;
    
    if (currentLang === 'ar') {
      document.body.classList.add('font-arabic');
    } else {
      document.body.classList.remove('font-arabic');
    }
  }, [lang]);

  return <>{children}</>;
};

// Composant pour vérifier l'existence de la base de données
const DatabaseCheck = ({ children }) => {
  const [isChecking, setIsChecking] = React.useState(true);
  const [dbExists, setDbExists] = React.useState(false);

  React.useEffect(() => {
    const checkDatabase = async () => {
      try {
        const usersCount = await db.users.count();
        setDbExists(usersCount > 0);
      } catch (error) {
        console.error('Error checking database:', error);
        setDbExists(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkDatabase();
  }, []);

  if (isChecking) {
    return <div>Chargement...</div>;
  }

  if (!dbExists) {
    return <Navigate to="/fr" replace />;
  }

  return children;
};

/**
 * Composant pour la route de login uniquement
 * Permet l'accès à tous les utilisateurs sans redirection
 */
const LoginRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return <div>Chargement...</div>;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Routes Admin */}
      {adminRoutes.map((route, index) => (
        <Route key={index} path={route.path} element={
          route.path === '/admin/login' ? (
            route.element
          ) : (
            <AdminRoute>
              {route.element}
            </AdminRoute>
          )
        }>
          {route.children?.map((child, childIndex) => (
            <Route key={childIndex} path={child.path} element={child.element} />
          ))}
        </Route>
      ))}

      {/* Route de login avec langue dynamique */}
      <Route path="/:lang/login" element={
        <LanguageLayout>
          <LoginRoute>
            <DatabaseCheck>
              <LoginPage />
            </DatabaseCheck>
          </LoginRoute>
        </LanguageLayout>
      } />

      {/* Routes publiques avec langue */}
      <Route path="/:lang" element={
        <LanguageLayout>
          <PublicRoute>
            <PublicLayout />
          </PublicRoute>
        </LanguageLayout>
      }>
        <Route index element={<Home />} />
        <Route path="contact" element={<Contact />} />
        <Route path="about" element={<div>À propos</div>} />
        <Route path="faq" element={<div>FAQ</div>} />
        <Route path="help" element={<div>Aide</div>} />
        <Route path="privacy" element={<div>Politique de confidentialité</div>} />
        <Route path="terms" element={<div>Conditions d'utilisation</div>} />
      </Route>

      {/* Routes MyApp protégées avec langue */}
      <Route path="/:lang/myapp" element={
        <LanguageLayout>
          <PrivateRoute>
            <MyAppLayout />
          </PrivateRoute>
        </LanguageLayout>
      }>
        {myAppRoutes.map((route, index) => (
          <Route key={index} path={route.path} element={route.element} />
        ))}
      </Route>

      {/* Route 404 - redirige vers la page d'accueil en français */}
      <Route path="*" element={<Navigate to="/fr" replace />} />
    </Routes>
  );
};

export default AppRoutes; 